// Copyright (c) Docugami, Inc. All rights reserved.

import { IContextualMenuItem, Shimmer, ShimmerElementType } from "@fluentui/react";
import { Property } from "hooks/documentProperties";
import styled from "styled-components";
import { PropertyList } from "./propertyList";
import { useMemo } from "react";
import { groupBy } from "utils/ArrayUtils";

const PropertiesContainer = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
`

interface DocumentPropertiesViewerProps {
    properties: Property[];
    isLoading?: boolean;
    onSelect: (p: Property) => void;
    onDelete: (p: Property) => void;
    canDrag: (labelId: string) => boolean;
    onDragStart: (labelId: string) => void;
    onDragEnd?: (labelId: string) => void;
    getCommands?: (labelId: string) => IContextualMenuItem[];
}

export const DocumentPropertiesViewer = (props: DocumentPropertiesViewerProps): JSX.Element => {
    const loadingItems: JSX.Element[] = [
        <LoadingPropertyItem key="loading_1" />,
        <LoadingPropertyItem key="loading_2" />,
        <LoadingPropertyItem key="loading_3" />,
    ];

    const groupedProperties = useMemo(() => Object.entries(groupBy(props.properties, x => x.labelId)).map(x => ({ key: x[0], items: x[1] })), [props.properties]);
    return (
        <PropertiesContainer>
            {!!props.isLoading ?
                loadingItems :
                groupedProperties.map(g => {
                    return (
                        <PropertyList
                            key={g.key}
                            labelId={g.key}
                            properties={g.items}
                            onClick={props.onSelect}
                            onDelete={props.onDelete}
                            isDraggable={props.canDrag(g.key)}
                            onDragStart={() => props.onDragStart(g.key)}
                            onDragEnd={() => props.onDragEnd?.(g.key)} 
                            commands={props.getCommands?.(g.key)} />
                    )
                })
            }
        </PropertiesContainer>
    )
}

const LoadingPropertyItem = (): JSX.Element => {
    return (
        <Shimmer
            styles={{
                root: {
                    borderRadius: 5
                }
            }}
            shimmerElements={[
                {
                    type: ShimmerElementType.line,
                    width: "100%",
                    height: 35
                }
            ]} />
    )
}
